const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type Day = typeof DAYS[number];

const DAY_NAMES: Record<Day, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

export type Schedule = {
  [K in Day as `${K}_start_at` | `${K}_end_at`]?: string;
};

function fmt12(t: string): string {
  const [hStr, mStr] = t.split(':');
  let h = parseInt(hStr), m = parseInt(mStr);
  const ampm = h < 12 ? 'am' : 'pm';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return m === 0 ? `${h}${ampm}` : `${h}:${String(m).padStart(2, '0')}${ampm}`;
}

function groupConsecutiveDays(days: Day[]): Day[][] {
  if (!days.length) return [];
  const groups: Day[][] = [];
  let group: Day[] = [days[0]];
  for (let i = 1; i < days.length; i++) {
    if (DAYS.indexOf(days[i]) === DAYS.indexOf(days[i - 1]) + 1) {
      group.push(days[i]);
    } else {
      groups.push(group);
      group = [days[i]];
    }
  }
  groups.push(group);
  return groups;
}

function formatDayGroup(group: Day[]): string {
  if (group.length === 1) return DAY_NAMES[group[0]];
  if (group.length === 2) return `${DAY_NAMES[group[0]]} and ${DAY_NAMES[group[1]]}`;
  return `${DAY_NAMES[group[0]]} to ${DAY_NAMES[group[group.length - 1]]}`;
}

export default function formatAvailability(schedule: Schedule): string {
  const availDays = DAYS.filter(
    d => schedule[`${d}_start_at`] && schedule[`${d}_end_at`]
  );
  if (!availDays.length) return 'Not available';

  const timeGroups = new Map<string, Day[]>();
  availDays.forEach(d => {
    const key = `${schedule[`${d}_start_at`]}-${schedule[`${d}_end_at`]}`;
    const existing = timeGroups.get(key) ?? [];
    timeGroups.set(key, [...existing, d]);
  });

  return Array.from(timeGroups.entries()).map(([key, days]) => {
    const [start, end] = key.split('-');
    const timeStr = `${fmt12(start)} to ${fmt12(end)}`;

    const sorted = [...days].sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));
    const isEveryDay = DAYS.every(d => days.includes(d));
    const isWeekdays = (['mon','tue','wed','thu','fri'] as Day[]).every(d => days.includes(d))
      && !(['sat','sun'] as Day[]).some(d => days.includes(d));
    const isWeekends = (['sat','sun'] as Day[]).every(d => days.includes(d))
      && !(['mon','tue','wed','thu','fri'] as Day[]).some(d => days.includes(d));

    let dayStr: string;
    if (isEveryDay)      dayStr = 'every day';
    else if (isWeekdays) dayStr = 'Monday to Friday';
    else if (isWeekends) dayStr = 'Saturday and Sunday';
    else dayStr = groupConsecutiveDays(sorted).map(formatDayGroup).join(', ');

    return `${dayStr}, ${timeStr}`;
  }).join(' · ');
}
